import type { ReactNode } from "react";
import React, { createContext, useContext } from "react";

import type { PageTracker } from "../integration/frontend-tracking-agent";
import type { CustomDimensionsRepository } from "./CustomDimensionsProvider";
import { CustomDimensionsProvider } from "./CustomDimensionsProvider";
import type { DatalayerTrackingClient } from "./DatalayerTrackingProvider";
import { DatalayerTrackingProvider } from "./DatalayerTrackingProvider";
import type { DataMeshTrackingClient } from "./DataMeshTrackingProvider";
import { DataMeshTrackingProvider } from "./DataMeshTrackingProvider";
import type { ECommerceTrackingClient } from "./ECommerceTrackingProvider";
import { ECommerceTrackingProvider } from "./ECommerceTrackingProvider";
import { PageTrackerProvider } from "./PageTrackerProvider";
import type { ThirdPartyTrackingAgent } from "./thirdPartyTrackingAgent";

export interface TrackingParamsRepository {
  getUtmParams(): Record<string, string>;
  getMktportalParams(): { mktportal: string };
}

const defaultTrackingParamsRepository: TrackingParamsRepository = {
  getUtmParams: () => ({}),
  getMktportalParams: () => ({ mktportal: "" }),
};

const defaultThirdPartyTrackingAgentContext = {
  trackSurvey: () => {},
  trackPage: () => {},
  setCustomDimension: () => {},
  trackEvent: () => {},
  trackFireEvent: () => {},
  trackQualtrics: () => {},
  trackWebVitals: () => ({
    handler: () => {},
  }),
};

const TrackingParamsRepositoryContext = createContext<TrackingParamsRepository>(
  defaultTrackingParamsRepository,
);
export const ThirdPartyTrackingAgentContext =
  createContext<ThirdPartyTrackingAgent>(defaultThirdPartyTrackingAgentContext);

export const useTrackingParamsRepository = () =>
  useContext(TrackingParamsRepositoryContext);
// TODO: remove in the next breaking change in favor of useTrackingParamsRepository
export const useTrackingFlow = useTrackingParamsRepository;

export interface TrackingProviderProps {
  pageTracker?: PageTracker;
  trackingParamsRepository: TrackingParamsRepository;
  thirdPartyTrackingAgent: ThirdPartyTrackingAgent;
  customDimensionsRepository: CustomDimensionsRepository;
  datalayerTrackingClient: DatalayerTrackingClient;
  eCommerceTrackingClient: ECommerceTrackingClient;
  dataMeshTrackingClient: DataMeshTrackingClient;
  children: ReactNode;
}

export const TrackingProvider = ({
  pageTracker,
  trackingParamsRepository,
  thirdPartyTrackingAgent,
  customDimensionsRepository,
  datalayerTrackingClient,
  eCommerceTrackingClient,
  dataMeshTrackingClient,
  children,
}: TrackingProviderProps) => (
  <PageTrackerProvider pageTracker={pageTracker}>
    <TrackingParamsRepositoryContext.Provider value={trackingParamsRepository}>
      <ThirdPartyTrackingAgentContext.Provider value={thirdPartyTrackingAgent}>
        <CustomDimensionsProvider
          customDimensionsRepository={customDimensionsRepository}
        >
          <DatalayerTrackingProvider
            datalayerTrackingClient={datalayerTrackingClient}
          >
            <ECommerceTrackingProvider
              eCommerceTrackingClient={eCommerceTrackingClient}
            >
              <DataMeshTrackingProvider
                dataMeshTrackingClient={dataMeshTrackingClient}
              >
                {children}
              </DataMeshTrackingProvider>
            </ECommerceTrackingProvider>
          </DatalayerTrackingProvider>
        </CustomDimensionsProvider>
      </ThirdPartyTrackingAgentContext.Provider>
    </TrackingParamsRepositoryContext.Provider>
  </PageTrackerProvider>
);
